import React, { useContext } from 'react';
import styled from 'styled-components';
import { AccessType } from '@bb/domain/dist/types';
import {
  Icon,
  Text,
  Select,
  InputTextArea,
  Input,
  RadioGroup,
  theme,
  Checkbox,
  Radio,
} from 'components';
import { AppointmentActionModalContext } from 'contexts';
import { VerticalSpace } from '@brokerbay/design-components';
import { featureEnabled } from 'utils';
import TimePicker from './TimePicker';
import {
  CoopCancelReason,
  LACancelReason,
  CancelReason,
  BeforeAfter,
} from './types';
import { AccessTypeMap } from './constants';

const SACancelOptions: Array<{ value: CoopCancelReason; label: string }> = [
  { value: 'reschedule', label: 'I will reschedule' },
  {
    value: 'notInterested',
    label: 'Client is no longer interested',
  },
  { value: 'custom', label: 'Other (Custom)' },
];

const LACancelOptions: Array<{ value: LACancelReason; label: string }> = [
  { value: 'anotherTime', label: 'Book for another time' },
  { value: 'anotherDay', label: 'Book for another day' },
  { value: 'custom', label: 'Other (Custom)' },
];

const DetailsRequired = () => (
  <DetailsRequiredContainer>
    <Icon type="antdWarningFilled" color="errorColor" width={12} height={12} />
    Details required
  </DetailsRequiredContainer>
);

type ConfirmDenySelectorProps = {
  beforeAfter: BeforeAfter;
  setBeforeAfter: React.Dispatch<React.SetStateAction<BeforeAfter>>;
  cancelReason: CancelReason;
  setCancelReason: React.Dispatch<React.SetStateAction<CancelReason>>;
  propertyAccess: {
    value: AccessType;
    initial: AccessType;
    set: React.Dispatch<React.SetStateAction<AccessType>>;
  };
  details: {
    value: string;
    initial: string;
    set: React.Dispatch<React.SetStateAction<string>>;
  };
  accessCode: {
    value: string;
    initial: string;
    set: React.Dispatch<React.SetStateAction<string>>;
  };
  isOneDayCodeEnabled: {
    value: boolean;
    set: React.Dispatch<React.SetStateAction<boolean>>;
  };
  isCancelOrDeny: boolean;
  canConfirm: boolean;
};

const ConfirmDenySelector = ({
  beforeAfter,
  setBeforeAfter,
  cancelReason,
  setCancelReason,
  propertyAccess,
  details,
  accessCode,
  isCancelOrDeny,
  canConfirm,
  isOneDayCodeEnabled,
}: ConfirmDenySelectorProps) => {
  const { agent, action, appointment } = useContext(
    AppointmentActionModalContext,
  );

  const buildTimePicker = () => (
    <TimePickerContainer>
      <BeforeAfterRadioGroup
        value={beforeAfter.beforeAfter}
        onChange={e =>
          setBeforeAfter(prev => ({ ...prev, beforeAfter: e.target.value }))
        }
      >
        <VerticalSpace size={8}>
          <Radio key="before" value="before">
            Before
          </Radio>
          <Radio key="after" value="after">
            After
          </Radio>
        </VerticalSpace>
      </BeforeAfterRadioGroup>
      <TimePicker time={beforeAfter.time} setTime={setBeforeAfter} />
    </TimePickerContainer>
  );

  const buildTitle = () => {
    let title = 'Access property by:';
    if (action === 'deny') title = 'Reason for denying';
    if (action === 'cancel') title = 'Reason for cancelling';

    return (
      <SelectTitle>
        {title}
        {((isCancelOrDeny && !canConfirm) ||
          (!isCancelOrDeny && !propertyAccess.value)) && <DetailsRequired />}
      </SelectTitle>
    );
  };

  const buildSecondaryTitle = () => {
    if (!cancelReason && propertyAccess.value) {
      let title = 'Instructions:';
      if (
        ['Lock Box', 'Door Code', 'SentriLock', 'MasterLock'].includes(
          propertyAccess.value,
        )
      )
        title = `${AccessTypeMap[propertyAccess.value].label} instructions:`;
      return (
        <SelectTitle>
          {title}
          {!canConfirm && <DetailsRequired />}
        </SelectTitle>
      );
    }
  };

  const buildPlaceholderText = () => {
    if (action === 'deny') return 'Why are you denying this showing request?';
    if (action === 'cancel')
      return 'Why are you cancelling this showing request?';
    return AccessTypeMap[propertyAccess.value].placeholder;
  };

  const selectOnChange = (value: CancelReason | AccessType) => {
    // If the user switches to a different access type, then returns to the initial access
    // type set on the appointmentConfig, restore the original details and access code.
    // Otherwise, clear them.
    details.set(
      action === 'confirm' && value === propertyAccess.initial
        ? details.initial
        : '',
    );
    accessCode.set(
      action === 'confirm' && value === propertyAccess.initial
        ? accessCode.initial
        : '',
    );
    if (isCancelOrDeny) {
      setCancelReason(value as CancelReason);
    } else {
      propertyAccess.set(value as AccessType);
    }
  };

  const buildSelectOptions = () => {
    const PropertyAccessOptions = Object.keys(AccessTypeMap)
      .filter((_propertyAccess: AccessType) =>
        [
          'Lock Box',
          'Direct',
          'Key',
          'Door Code',
          appointment.appointmentConfig?.access,
        ]
          .filter(Boolean)
          .includes(_propertyAccess),
      )
      .map((_propertyAccess: AccessType) => {
        const { value, label } = AccessTypeMap[_propertyAccess];
        if (value === 'Lock Box')
          return { value, label: 'Combination Lock Box' };
        return { value, label };
      });

    if (isCancelOrDeny) {
      return agent === 'coop' ? SACancelOptions : LACancelOptions;
    }
    return PropertyAccessOptions;
  };

  const buildCodeEntry = () => {
    const disabled = [
      'SentriLock',
      'SentriLockV2',
      'SentriLock App',
      'MasterLock',
    ].includes(propertyAccess.value);
    if (['Door Code', 'Lock Box', 'MasterLock'].includes(propertyAccess.value))
      return (
        <StyledInput
          disabled={disabled}
          placeholder={AccessTypeMap[propertyAccess.value].codePlaceholder}
          value={accessCode.value}
          onChange={e => accessCode.set(e.target.value)}
        />
      );
  };

  const buildTextarea = () => {
    if (cancelReason === 'custom' || propertyAccess.value)
      return (
        <StyledInputTextArea
          placeholder={buildPlaceholderText()}
          rows={4}
          value={details.value}
          onChange={e => details.set(e.target.value)}
          dataCy="textarea-custom-reason"
        />
      );
  };

  const buildManualCodeCheckbox = () => {
    if (propertyAccess.value !== 'MasterLock') return null;

    return (
      <StyledCheckbox
        onChange={e => isOneDayCodeEnabled.set(e.target.checked)}
        checked={isOneDayCodeEnabled.value}
      >
        Enable Manual Code
      </StyledCheckbox>
    );
  };

  return (
    <StyledSelector>
      {buildTitle()}
      <Select
        value={isCancelOrDeny ? cancelReason : propertyAccess.value}
        onChange={selectOnChange}
        options={buildSelectOptions()}
        placeholder={isCancelOrDeny && 'Select a reason'}
        getPopupContainer={el => el.parentElement}
        dataCy="select-reason"
      />
      {cancelReason === 'anotherTime' && buildTimePicker()}
      {buildSecondaryTitle()}
      {buildCodeEntry()}
      {buildManualCodeCheckbox()}
      {buildTextarea()}
    </StyledSelector>
  );
};

export default ConfirmDenySelector;

const DetailsRequiredContainer = styled.div`
  display: flex;
  align-items: center;
  font-weight: 400;
  font-size: 12px;
  color: ${theme.red5};
  gap: 8px;
`;

const TimePickerContainer = styled.div`
  display: flex;
  margin-top: 16px;
`;

const BeforeAfterRadioGroup = styled(RadioGroup)`
  margin-right: auto;
`;

const StyledSelector = styled.div`
  margin-bottom: 40px;
`;

const SelectTitle = styled(Text)`
  display: flex;
  justify-content: space-between;
  margin: 16px 0 8px 0;
`;

const StyledInput = styled(Input)<{ disabled: boolean }>`
  margin-top: 8px;
  & .ant-input {
    ${({ disabled }) =>
      disabled && `background-color: ${theme.gray5}; color: rgba(0,0,0,0.85);`}
  }
`;

const StyledInputTextArea = styled(InputTextArea)`
  margin-top: 8px;
`;

const StyledCheckbox = styled(Checkbox)`
  margin: 5px 0;
`;
